import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router";
import { Typography, Box, Alert, Button, Icon, Card } from "@mui/material";

import DashboardLayout from "../LayoutContainers/DashboardLayout";
import DashboardNavbar from "../Navbars/DashboardNavbar";
import Footer from "../Footer";
import MDBox from "../MDBox";
import Api from "../../Api/Api";
import colors from "../../colors";
import { useMaterialUIController } from "../../context";
import { setActiveQuiz, useUserController } from "../../context/user";
import {useNavigate} from "react-router-dom";

const VideoPlayer = () => {
  const { id } = useParams();
  const videoRef = useRef(null);
  const intervalRef = useRef(null);
  const maxWatchedRef = useRef(0);   // furthest position actually watched (seek-lock high-water mark)
  const playerRef = useRef(null);    // wrapper element used for fullscreen (keeps custom controls)
  const [video, setVideo] = useState(null);
  const [videoLog, setVideoLog] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [finished, setFinished] = useState(null);
  const [resumed, setResumed] = useState(false);
  const [play, setPlay] = useState(false);
  const [watchStatus, setWatchStatus] = useState(null);
  const startTime = useRef(null);

  const [controller] = useMaterialUIController();
  const [userController, userDispatch] = useUserController();
  const { user } = userController;
  const { spanish } = controller;

  let navigate = useNavigate();

  useEffect(() => {
    if (id && user && !videoLog) {
      Api.getVideoLog(user.id, id)
        .then(({ data }) => setVideoLog(data || {}))
        .catch(console.error);
    }
  }, [id, user, videoLog]);

  useEffect(() => {
    if (id && !quiz) {
      Api.getOneQuiz(id).then(({ data }) => {
        const q = data[0];
        const vid = spanish && q.spanish ? q.spanish : q.english;
        setQuiz(q);
        setVideo({ url: vid.video, title: vid.title });
      });
    }
  }, [id, spanish, quiz]);

  useEffect(() => {
    if (quiz) {
      const vid = spanish && quiz.spanish ? quiz.spanish : quiz.english;
      setVideo({ url: vid.video, title: vid.title });
    }
  }, [spanish, quiz]);

  const playPause = () => {
    const el = videoRef.current;
    setPlay(prev => {
      const newPlay = !prev;
      if (newPlay) {
        if (!startTime.current) startTime.current = new Date();
        el.play();
        setActiveQuiz(userDispatch, {
          id,
          videoId: id,
          userId: user.id,
          videoName: video.title,
          videoStartTime: startTime.current,
        });
      } else {
        el.pause();
        setActiveQuiz(userDispatch, {
          id,
          videoId: id,
          userId: user.id,
          videoPauseTime: new Date(),
          lastVideoPausedTime: el.currentTime,
        });
      }
      return newPlay;
    });
  };

  const videoFinished = () => {
    setFinished(true);
    setPlay(false);
    const el = videoRef.current;
    setActiveQuiz(userDispatch, {
      id,
      videoId: id,
      userId: user.id,
      videoEndTime: new Date(),
      ended: true,
      // Final position so the server can confirm the playhead actually reached the end.
      // `ended` stays as a UX hint (button/alert); the real gate is the server-computed
      // watchVerified, which the client cannot assert.
      currentTime: el ? el.currentTime : undefined,
      durationSec: el && Number.isFinite(el.duration) ? el.duration : undefined,
    });
  };

  // --- Seek lockdown (Samsung skip fix) ----------------------------------------------------
  // The player exposes ONLY the custom Play/Pause button; the native `controls` bar (and its
  // scrubber) is removed from the <video>. These guards are the cross-device backstop for every
  // OTHER seek surface the page cannot hide via HTML: Samsung Internet's Video Assistant overlay,
  // Android/iOS lock-screen & notification media controls, hardware/keyboard keys, and devtools.
  // The high-water mark only ever advances during real forward playback, so the playhead can
  // never jump PAST what was actually watched. Re-watching (seeking backward) stays allowed.
  const SEEK_TOLERANCE_SEC = 0.5; // absorbs normal drift + documented iOS 13+ ~15ms back-jumps

  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (el && !el.seeking && el.currentTime > maxWatchedRef.current) {
      maxWatchedRef.current = el.currentTime;
    }
  };

  const handleSeeking = () => {
    const el = videoRef.current;
    if (!el) return;
    // Any forward jump beyond the watched high-water mark is snapped back. The snap-back sets
    // currentTime to maxWatched, so its own `seeking` event has delta 0 and never re-triggers.
    if (el.currentTime > maxWatchedRef.current + SEEK_TOLERANCE_SEC) {
      el.currentTime = maxWatchedRef.current;
    }
  };

  const handleRateChange = () => {
    const el = videoRef.current;
    if (el && el.playbackRate !== 1) el.playbackRate = 1; // no fast-forward via playback speed
  };

  const takeQuiz = () => {
    navigate(`/quiz?quizId=${id}`)
  };

  const fullScreen = () => {
    // Fullscreen the player WRAPPER, not the bare <video>: requesting fullscreen on the video
    // element makes the browser re-show its native control bar (incl. a scrubber) in fullscreen
    // on several platforms, which would reopen the skip path. The wrapper keeps our custom
    // play/pause-only UI in fullscreen.
    const el = playerRef.current || videoRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  };

  const iOS = () => {
    const platform = navigator.platform;
    return /iPhone|iPad|iPod/.test(platform) || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
  };

  const sendPlaybackPing = () => {
    const el = videoRef.current;
    if (!el) return;
    setActiveQuiz(userDispatch, {
      id,
      videoId: id,
      userId: user.id,
      videoPauseTime: new Date(),
      lastVideoPausedTime: el.currentTime,
      // Watch-gate signal: playhead + runtime so the server can measure real elapsed
      // playback. The interval only fires while the video is playing, so this credits
      // genuine watch time, not idle/paused time.
      currentTime: el.currentTime,
      durationSec: Number.isFinite(el.duration) ? el.duration : undefined,
    });
  };

  // Best-effort flush of the final position when the page is hidden/closed (iOS screen-lock,
  // tab backgrounding, navigation). Preserves the tail of a genuine watch so multi-session /
  // interrupted watches still accumulate (plan §4.3). sendBeacon survives unload; the axios
  // ping would not.
  const flushBeacon = useCallback(() => {
    const el = videoRef.current;
    if (!el || !user || !navigator.sendBeacon) return;
    try {
      const base = (process.env.REACT_APP_APP_API_URL || "").replace(/\/$/, "");
      const inst = process.env.REACT_APP_APP_INSTANCE;
      const url = `${base}/api/${inst}/videos/log`;
      const payload = JSON.stringify({
        userId: user.id,
        videoId: id,
        currentTime: el.currentTime,
        durationSec: Number.isFinite(el.duration) ? el.duration : undefined,
        lastVideoPausedTime: el.currentTime,
      });
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } catch (e) {
      /* best-effort only */
    }
  }, [id, user]);

  const startInterval = () => {
    if (!intervalRef.current) intervalRef.current = setInterval(sendPlaybackPing, 5000);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    const el = videoRef.current;
    if (el) {
      el.addEventListener("play", startInterval);
      el.addEventListener("pause", stopInterval);
      el.addEventListener("ended", stopInterval);
      return () => {
        el.removeEventListener("play", startInterval);
        el.removeEventListener("pause", stopInterval);
        el.removeEventListener("ended", stopInterval);
        stopInterval();
      };
    }
  }, []);

  // Neutralize OS-level media controls so the lock screen / notification shade / hardware keys
  // cannot seek or skip (these sit ABOVE the page and ignore HTML attributes like controlsList).
  // Null handlers hide the seek/skip buttons entirely; play/pause is intentionally left to the OS.
  useEffect(() => {
    if (!("mediaSession" in navigator)) return undefined;
    const seekActions = ["seekto", "seekforward", "seekbackward", "previoustrack", "nexttrack"];
    seekActions.forEach((action) => {
      try {
        navigator.mediaSession.setActionHandler(action, null);
      } catch (e) {
        /* action not supported in this browser — ignore */
      }
    });
    return undefined;
  }, []);

  // Flush the final watched position on page hide / unload (best-effort).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushBeacon();
    };
    window.addEventListener("pagehide", flushBeacon);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flushBeacon);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [flushBeacon]);

  // Poll the server watch-gate status. Used only to (optionally) gate the "Take Quiz" button
  // in enforce mode. FAILS OPEN — any error leaves watchStatus null, which the render treats
  // as "allowed". In observe mode the server returns canProceed:true, so this is a no-op.
  useEffect(() => {
    if (!id || !user) return undefined;
    let cancelled = false;
    const fetchStatus = () => {
      Api.getWatchStatus(user.id, id)
        .then(({ data }) => { if (!cancelled) setWatchStatus(data || null); })
        .catch(() => { if (!cancelled) setWatchStatus(null); });
    };
    fetchStatus();
    // Re-check shortly after the user finishes so a just-earned verification reflects quickly.
    const t1 = setTimeout(fetchStatus, 3000);
    const t2 = setTimeout(fetchStatus, 8000);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); };
  }, [id, user, finished]);

  // Enforce-mode gate (FAILS OPEN): only true when the server is certain this watch is
  // unverified under enforcement. null status / observe mode / unknown duration → false, so
  // a good user is never blocked. In observe mode this is always false (legacy behavior).
  const blockedByGate = !!(watchStatus && watchStatus.enforcement === "enforce" && watchStatus.canProceed === false);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={0}>
        <Card>
          <Alert severity="warning">
            {spanish
              ? "Este video no se puede adelantar ni omitir. Debe verlo completo para continuar."
              : "This video cannot be skipped or fast-forwarded. Please watch it in full to proceed."}
          </Alert>
          <Alert severity="info">
            {spanish ? "Instrucciones" : "Instructions"}
            <ul style={{ paddingLeft: 25 }}>
              <li>{spanish ? "Haz clic en Iniciar para ver el video completo." : "Click Start to watch the full video."}</li>
              <li>{spanish ? "Después del video, podrás acceder al cuestionario." : "After the video, you will be able to access the quiz."}</li>
              <li>{spanish ? "Se requiere un 70% o más para aprobar y recibir tu certificado." : "A 70% score or higher is required to pass and receive your certificate."}</li>
            </ul>
          </Alert>
          <MDBox p={3} lineHeight={1} height="72vh" sx={{ overflow: "scroll", display: "flex", justifyContent: "flex-start", alignItems: "center", flexDirection: "column", paddingTop: 6 }}>
            <Box maxWidth="1200px">
              {video && (
                <>
                  <Typography>{video.title}</Typography>
                  <Box component="section" ref={playerRef} sx={{ maxWidth: "700px", marginTop: 1, marginBottom: 4 }}>
                    <video
                      ref={videoRef}
                      src={video.url}
                      playsInline
                      width="100%"
                      disablePictureInPicture
                      onEnded={videoFinished}
                      onSeeking={handleSeeking}
                      onTimeUpdate={handleTimeUpdate}
                      onRateChange={handleRateChange}
                      onContextMenu={e => e.preventDefault()}
                      controlsList="nodownload nofullscreen noremoteplayback"
                      x-webkit-airplay="deny"
                    />
                    <Box sx={{ border: "1px solid #bababc", display: "flex", justifyContent: "space-between", alignItems: "center", padding: 1, backgroundColor: colors.Theme.lightBlue }}>
                      <Button variant="contained" onClick={playPause}>
                        <Icon fontSize="large" style={{ marginRight: 5 }}>{play ? "pause" : "play_arrow"}</Icon>
                        {spanish ? (play ? "Pausar video" : "Reproducir video") : (play ? "Pause Video" : "Play Video")}
                      </Button>
                      {(finished || videoLog?.ended) && !blockedByGate && (
                        <Button variant="contained" onClick={takeQuiz}>
                          {spanish ? "Iniciar cuestionario" : "Take Quiz"}
                        </Button>
                      )}
                      {!iOS() && (
                        <Button variant="contained" onClick={fullScreen}>
                          <Icon fontSize="large" style={{ marginRight: 5 }}>fullscreen</Icon>
                          {spanish ? "Pantalla completa" : "Full Screen"}
                        </Button>
                      )}
                    </Box>
                    {blockedByGate && (
                      <Alert severity="info" sx={{ marginTop: 1 }}>
                        {spanish
                          ? "Para continuar con el cuestionario, primero debes ver el video completo."
                          : "Please watch the full video before continuing to the quiz."}
                      </Alert>
                    )}
                    {videoLog?.ended && !blockedByGate && (
                      <Alert severity="success" sx={{ marginTop: 1 }}>
                        {spanish
                          ? "Ya has visto este video. Puedes volver a verlo o continuar con el cuestionario."
                          : "You have already completed this video. You may rewatch it or proceed to the quiz."}
                      </Alert>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default VideoPlayer;
